// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import NumberInput from '../../../../theme/inputs/NumberInput';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, InputBase, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../../../../theme';
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import { useGetSuppliersSelectQuery } from '../../../../../newapi/global/supplier/supplierSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShDateRangePicker from "../../../../theme/ShDateRangePicker";
import { ENV_TYPES } from "../../../Sidebar";
import { useSelector } from 'react-redux';
import { formatPrice } from '../../../../../utils/helpers';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t, i18n} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    const selectedId = useSelector((state) => state.settings.environment.selectedId);

    // The inbound's supplier's products plus products without a supplier; without a supplier every product of the relation.
    const { data: products } = useGetProductsSelectQuery(
        {relationId: isRelation ? selectedId : props.data?.relationId, 
            supplierId: props.data?.supplierId ?? null,
            addStock: true, 
            ignoreVirtual: true
        },
        { skip: !isRelation && !props.data?.relationId }
    );

    // Same query as the details step, so this is served from cache.
    const { data: suppliersData } = useGetSuppliersSelectQuery(
        props.data?.relationId ? props.data.relationId : null,
        { skip: !props.data?.supplierId }
    );
    const selectedSupplier = suppliersData?.find(item => item.id === props.data?.supplierId);

    const [productOptions, setProductOptions] = useState([]);

    useEffect(() => {
        if (products) {
            const mappedProducts = products.map(product => ({
                id: product.sku,
                name: product.combined,
                barcodes:product.barcodes,
                serialNumberRequired: product.serialNumberRequired,
                batchRequired: product.batchRequired,
                lotRequired: product.lotRequired,
                purchasePrice: parseFloat(product.purchasePrice) || 0
            }));

            setProductOptions(mappedProducts);
        }
    }, [products]);
    
    const [newInboundLines, setNewInboundLines] = useState([{
        isDescriptionRow: false,
        description: "",
        sku: "",
        amountForecasted: 0,
        serialNumbers: [],
        batch: "",
        lot: "",
        bbDate: "",
        index: 1
    }]);
    
    useEffect(() => {
        // Set the newInboundLines state to the data.inboundLines state
        if (data.inboundLines && data.inboundLines.length > 0) {
            const linesCopy = data.inboundLines.map((line, index) => ({
                ...line,
                index: index + 1
            }));
            
            linesCopy.push({
                isDescriptionRow: false,
                description: "",
                sku: "",
                amountForecasted: 0,
                serialNumbers: [],
                batch: "",
                lot: "",
                bbDate: "",
                index: linesCopy[linesCopy.length - 1].index + 1
            })

            setNewInboundLines(linesCopy);
        }
    },[]);

    const handleChange = (index, key, value) => {
        const inboundLinesCopy = [...newInboundLines];
        inboundLinesCopy[index - 1][key] = value;

        // When amount changes and product requires serial numbers, adjust serialNumbers array
        if (key === 'amountForecasted') {
            const line = inboundLinesCopy[index - 1];
            const product = productOptions.find(p => p.id === line.sku);
            if (product?.serialNumberRequired) {
                const currentSerialNumbers = line.serialNumbers || [];
                const newAmount = parseInt(value) || 0;

                // Adjust array length to match amount
                if (newAmount > currentSerialNumbers.length) {
                    // Add empty strings for new inputs
                    line.serialNumbers = [...currentSerialNumbers, ...Array(newAmount - currentSerialNumbers.length).fill('')];
                } else if (newAmount < currentSerialNumbers.length) {
                    // Remove excess serial numbers
                    line.serialNumbers = currentSerialNumbers.slice(0, newAmount);
                }
            }
        }

        if (inboundLinesCopy[inboundLinesCopy.length - 1].sku.length > 0 || inboundLinesCopy[inboundLinesCopy.length - 1].description.length > 0) {
            inboundLinesCopy.push({
                isDescriptionRow: false,
                description: "",
                sku: "",
                amountForecasted: 0,
                serialNumbers: [],
                batch: "",
                lot: "",
                bbDate: "",
                index: inboundLinesCopy[inboundLinesCopy.length - 1].index + 1
            });
        }

        setNewInboundLines(inboundLinesCopy);
    };

    const handleProductSelect = (key, val) => {
        const lineIndex = parseInt(key.split(".")[0]);
        const lineKey = key.split(".")[1];

        // Initialize serial numbers array if product requires it
        const product = productOptions.find(p => p.id === val);
        if (product?.serialNumberRequired) {
            const inboundLinesCopy = [...newInboundLines];
            const line = inboundLinesCopy[lineIndex - 1];
            const amount = line.amountForecasted || 0;
            line.serialNumbers = Array(amount).fill('');
            inboundLinesCopy[lineIndex - 1] = line;
            setNewInboundLines(inboundLinesCopy);
        }

        handleChange(lineIndex, lineKey, val);
    }

    const handleSerialNumberChange = (lineIndex, serialIndex, value) => {
        const inboundLinesCopy = [...newInboundLines];
        const line = inboundLinesCopy[lineIndex - 1];
        const updatedSerialNumbers = [...(line.serialNumbers || [])];
        updatedSerialNumbers[serialIndex] = value;
        inboundLinesCopy[lineIndex - 1] = {
            ...line,
            serialNumbers: updatedSerialNumbers
        };
        setNewInboundLines(inboundLinesCopy);
    }
    
    useEffect(() => {
        const mappedInboundLines = newInboundLines
        .filter(x => (x.sku.length > 0 && x.amountForecasted > 0) || x.description.length > 0)
        .map(x => ({
            isDescriptionRow: x.isDescriptionRow,
            description: x.description,
            sku: x.sku,
            amountForecasted: x.amountForecasted,
            serialNumbers: x.serialNumbers || [],
            batch: x.batch || "",
            lot: x.lot || "",
            bbDate: x.bbDate || ""
        }));

        handleInput("inboundLines", mappedInboundLines);
    },[newInboundLines]);
      
    const handleDeleleteLineRow = (index) => {
        const updatedInboundLines = newInboundLines.filter(line => line.index !== index);
        setNewInboundLines(updatedInboundLines);
    }

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
    };

    // Forecasted amount times the product's purchase price, summed over all lines.
    const totalPurchaseValue = newInboundLines.reduce((total, line) => {
        const product = productOptions.find(p => p.id === line.sku);
        return total + (parseInt(line.amountForecasted) || 0) * (product?.purchasePrice ?? 0);
    }, 0);
    // The totals are only shown for a supplier with a minimum order value.
    const minimumOrderValue = parseFloat(selectedSupplier?.minimumOrderValue) || 0;

    return (
        <Box
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                borderTopLeftRadius:16,
                borderTopRightRadius:16,
                display:"flex",
                flexDirection:"column",
                gap:1,
                "@media screen and (max-width: 47.9375em)" : {

                }
            }}
        >
            {selectedSupplier && (
                <Text light>{t("inbound_products_from_supplier", { name: selectedSupplier.name })}</Text>
            )}
            {newInboundLines.map((inboundLine, index) => (
                <React.Fragment key={"new-inbound-line-"+index}>
                <Box flex={1}
                    sx={{
                        display:"flex",
                        gap:4,
                        justifyContent:"flex-start",
                        "@media screen and (max-width: 47.9375em)" : {
                            backgroundColor:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            boxShadow:theme.palette.mode === "dark" ? "none" : "0px 2px 4px  rgba(0, 0, 0, 0.1)",
                            border:theme.palette.mode === "dark" ? "none" : "1px solid "+colors.grey[100],
                            borderRadius:4,
                            py:1.5,
                            px:1.5,
                            flexDirection:"column",
                            alignItems:"flex-start",
                            gap:2,
                            overflow:"hidden",
                        }
                    }}
                >
                    <Box 
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:1,
                            "@media screen and (max-width: 47.9375em)" : {
                                overflow:"hidden",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width:60,
                                flexShrink:0,
                            }}
                        >
                            <Text light>{t("sku")}</Text>
                        </Box>
                        <ShValidatedInputDropdown
                            height={40}
                            tabIndex={-1}
                            key={"new-inboundLine-product-"+inboundLine.index}
                            displayName={"name"} 
                            changeField={"id"}
                            valueKey={"id"}
                            closeOnSelection
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search_product_placeholder"),
                                searchables: ["barcodes", "id"]
                            }}
                            styles={{
                                minWidth:300,
                                "@media screen and (max-width: 47.9375em)" : {
                                    minWidth:"unset",
                                }
                            }}
                            maxWidth={400}

                            disallowDeselection
                            name={`${inboundLine.index}.sku`}
                            selected={productOptions?.find(item => item.id && item.id === inboundLine.sku)?.id ?? -1}
                            options={productOptions ? productOptions : []}
                            noSelection={t("choose_option")} 
                            onChange={handleProductSelect}
                            error={props.errors.sku}
                        />
                        {(inboundLine.index > 1 || newInboundLines.length > 1) ?
                            <Box 
                                tabIndex={-1}
                                sx={{
                                    width:40,
                                    display:"none",
                                    justifyContent:"flex-end",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"flex",
                                    }
                                }}
                            >
                                <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleleteLineRow(inboundLine.index)} sx={{p:1}}>
                                    <DeleteIcon/>
                                </IconButton>
                            </Box> 
                            : null
                        }
                        
                    </Box>
                    <Box 
                        sx={{
                            width:280,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:1,
                            "@media screen and (max-width: 47.9375em)" : {
                                width:"unset",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width:64,
                                flexShrink:0,
                            }}
                        >
                            <Text light>{t("quantity")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                // width:144,
                                // backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                // borderRadius:4,
                                display:"flex",
                                // height:32
                            }}
                        >
                            <NumberInput btnSize={"24"}
                            inputSX={{
                                width:144,
                                "@media screen and (max-width: 47.9375em)" : {
                                    px:1,
                                    flex:1,
                                    width:"unset",
                                }
                            }}
                                value={inboundLine.amountForecasted}
                            change={(e) => handleChange(inboundLine.index, 'amountForecasted', e)}/>

                            {(inboundLine.index > 1 || newInboundLines.length > 1) ?
                                <Box 
                                    tabIndex={-1}
                                    sx={{
                                        width:40,
                                        display:"flex",
                                        justifyContent:"flex-end",
                                        "@media screen and (max-width: 47.9375em)" : {
                                            display:"none",
                                        }
                                    }}
                                >
                                    <IconButton tabIndex={-1} type="button" color="red" onClick={() => handleDeleleteLineRow(inboundLine.index)} sx={{p:1}}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </Box> 
                                : <Box tabIndex={-1} width={40} sx={{

                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:"none",
                                    }
                                }}/>
                            }
                        </Box>


                    </Box>
                </Box>
                {/* Batch/Lot Inputs */}
                {(() => {
                    const product = productOptions.find(p => p.id === inboundLine.sku);
                    const requiresBatch = product?.batchRequired;
                    const requiresLot = product?.lotRequired;
                    const showBatchLotContainer = (requiresBatch || requiresLot) && inboundLine.sku;

                    if (!showBatchLotContainer) return null;

                    const handleBatchLotChange = (field, value) => {
                        const inboundLinesCopy = [...newInboundLines];
                        inboundLinesCopy[index][field] = value;
                        setNewInboundLines(inboundLinesCopy);
                    };

                    const selectBbDate = (date) => {
                        const formattedDate = date[0].startDate.toISOString().split('T')[0];
                        handleBatchLotChange('bbDate', formattedDate);
                    };

                    // Determine the title based on what's required
                    let titleKey = "batch_lot_information";
                    if (requiresBatch && requiresLot) {
                        titleKey = "batch_lot_information";
                    } else if (requiresLot) {
                        titleKey = "lot_information";
                    } else if (requiresBatch) {
                        titleKey = "batch_information";
                    }

                    return (
                        <Box
                            sx={{
                                width: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                pl: 2,
                                py: 1.5,
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[250] : colors.grey[50],
                                borderRadius: 2,
                                "@media screen and (max-width: 47.9375em)": {
                                    pl: 1.5,
                                }
                            }}
                        >
                            <Text light semibold>{t(titleKey)}</Text>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                }}
                            >
                                {requiresBatch && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            "@media screen and (max-width: 47.9375em)": {
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                            }
                                        }}
                                    >
                                        <Text light sx={{ width: 120, flexShrink: 0 }}>
                                            {t("batch")}:
                                        </Text>
                                        <InputBase
                                            value={inboundLine.batch || ''}
                                            onChange={(e) => handleBatchLotChange('batch', e.target.value)}
                                            placeholder={t("enter_batch")}
                                            sx={{
                                                flex: 1,
                                                maxWidth: 400,
                                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                fontSize: 14,
                                                "@media screen and (max-width: 47.9375em)": {
                                                    width: "100%",
                                                    maxWidth: "unset",
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                                {requiresLot && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            "@media screen and (max-width: 47.9375em)": {
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                            }
                                        }}
                                    >
                                        <Text light sx={{ width: 120, flexShrink: 0 }}>
                                            {t("lot")}:
                                        </Text>
                                        <InputBase
                                            value={inboundLine.lot || ''}
                                            onChange={(e) => handleBatchLotChange('lot', e.target.value)}
                                            placeholder={t("enter_lot")}
                                            sx={{
                                                flex: 1,
                                                maxWidth: 400,
                                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                fontSize: 14,
                                                "@media screen and (max-width: 47.9375em)": {
                                                    width: "100%",
                                                    maxWidth: "unset",
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        "@media screen and (max-width: 47.9375em)": {
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                        }
                                    }}
                                >
                                    <Text light sx={{ width: 120, flexShrink: 0 }}>
                                        {t("best_before_date")}:
                                    </Text>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            maxWidth: 400,
                                            backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                            borderRadius: 1,
                                            display: "flex",
                                            height: 32,
                                            "@media screen and (max-width: 47.9375em)": {
                                                width: "100%",
                                                maxWidth: "unset",
                                            }
                                        }}
                                    >
                                        <ShDateRangePicker
                                            singleSelect={true}
                                            startDate={inboundLine.bbDate || undefined}
                                            select={selectBbDate}
                                            size={32}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    );
                })()}
                {/* Serial Number Inputs */}
                {(() => {
                    const product = productOptions.find(p => p.id === inboundLine.sku);
                    const requiresSerialNumbers = product?.serialNumberRequired && inboundLine.amountForecasted > 0;

                    if (!requiresSerialNumbers) return null;

                    return (
                        <Box
                            sx={{
                                width: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                pl: 2,
                                py: 1.5,
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[250] : colors.grey[50],
                                borderRadius: 2,
                                "@media screen and (max-width: 47.9375em)": {
                                    pl: 1.5,
                                }
                            }}
                        >
                            <Text light semibold>{t("serial_numbers")}</Text>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {(inboundLine.serialNumbers || []).map((serialNumber, serialIndex) => (
                                    <Box
                                        key={`serial-${inboundLine.index}-${serialIndex}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Text light sx={{ width: 120, flexShrink: 0 }}>
                                            {t("serial_number")} {serialIndex + 1}:
                                        </Text>
                                        <InputBase
                                            value={serialNumber || ''}
                                            onChange={(e) => handleSerialNumberChange(inboundLine.index, serialIndex, e.target.value)}
                                            placeholder={`${t("enter_serial_number")} ${serialIndex + 1}`}
                                            sx={{
                                                flex: 1,
                                                maxWidth: 400,
                                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                fontSize: 14,
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    );
                })()}
                </React.Fragment>
            ))}
            {minimumOrderValue > 0 && (
                <Box
                    sx={{
                        display:"flex",
                        flexDirection:"column",
                        alignItems:"flex-end",
                        gap:0.5,
                        mt:1,
                        pt:1.5,
                        pr:5,
                        borderTop:`1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                        "@media screen and (max-width: 47.9375em)" : {
                            pr:0,
                            alignItems:"stretch",
                        }
                    }}
                >
                    <Box sx={{ display:"flex", justifyContent:"space-between", gap:2 }}>
                        <Text light>{t("minimum_order_value")}</Text>
                        <Text light>{formatPrice(i18n, minimumOrderValue)}</Text>
                    </Box>
                    <Box sx={{ display:"flex", justifyContent:"space-between", gap:2 }}>
                        <Text light>{t("total_purchase_value")}</Text>
                        <Text semibold variant={totalPurchaseValue < minimumOrderValue ? "red" : undefined}>
                            {formatPrice(i18n, totalPurchaseValue)}
                        </Text>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const InboundLines = (props) => {
    const {t} = useTranslation();
    // Inkoopadvies adds a mail preview step for suppliers that do not get every inbound mailed (see Details).
    const supplierMailStep = useSelector((state) => !!state.progressiontabs.data?.supplierMailStep);
    return (
        <ProgressionTab 
        tabId="tabInboundLines" 
        subtitle={t("inboundlines")} 
        nextTab={"tabShipments"}
        step={2}
        maxSteps={supplierMailStep ? 4 : 3}
    >
        <Content />
    </ProgressionTab>
    )
}

export default InboundLines;

